/**
 * The voice: the model speaking to the reader about what just happened.
 *
 * After a pass a person pressed finishes, and after a run they started ends, a
 * small model call turns the session's own record of it into two short
 * paragraphs in the feed (three, for a run's one wrap-up) — what landed and what
 * it changes. This is the difference between a transcript and a conversation:
 * the feed already SHOWED everything, and nothing in it ever addressed the
 * reader.
 *
 * WHAT IT DOES NOT SAY. What to do next. The coach names that, by rules it can
 * state (invariant 8); a suggestion from the voice was a second recommender on
 * the same screen that could not say why, speaking last (D295). So the voice
 * summarises, is offered no buttons to fill, and its own replies carry none.
 *
 * WHERE IT SPEAKS. On the ask channel, with all of that channel's rules: no
 * search, no citations, nothing it says lands on the graph, and every receipt
 * on the same ledger through `engine/chat.ts`'s own payer machinery. Its turns
 * are `voice` turns in the feed conversation (`askLive.speakOn`) — the model's
 * lane, no question above them, never an input to the promote row.
 *
 * WHAT AUTHORISES THE SPEND. Every model call in this app sits behind an
 * explicit press (invariant 9), and the narrator's press is the one that ran
 * the pass it narrates: the narration is a disclosed follower of that
 * authorisation, the way a run's closing scan follows the run press. It is
 * announced in the feed's own note beside an off switch that persists, it
 * never fires for a pass nobody paid for, and it is one bounded low-effort
 * call — never a chain.
 *
 * WHEN IT STAYS QUIET. While a run is live (the run gets ONE wrap-up at the
 * end — a voice line per move would rebuild the wall the run message was built
 * to prevent); for a pass that errored or was refused (the record's own note
 * is the news); for a pass that landed nothing new — no node, and none of the
 * records its kind exists to write (`passLanded`), read off the record and
 * never off the model; for anything already narrated; for stored history a
 * reload brings back (every trigger is a TRANSITION this tab watched happen,
 * never a state found lying there); and whenever the voice is off or the chat
 * role has no key — a routed role with no key refuses, it never falls back
 * (invariant 4).
 *
 * WHY `ui/` AND NOT `engine/`. It writes into `askLive`'s live conversation,
 * which is UI-layer state, and the engine imports nothing from `ui/`. It is
 * `promotePress.ts`'s kind of module: a plain-`.ts` dispatcher beside the
 * components, testable without a renderer.
 */

import {
  LINK_READING_LABEL,
  linkReadingNote,
  OP_LABEL,
  OP_PAST,
  READING_LABEL,
  readingCaveat,
  readingNote,
  RUN_STOP_HEAD,
  moveResult,
  type Audit,
  type Bridge,
  type FieldSurvey,
  type Op,
  type Outcome,
  type PanelCheck,
  type ReasonNode,
  type Run,
  type Session,
  type Verification,
  kindLabelOf,
} from '../core/types'
import type { StoredAbout, StoredTurn } from '../core/conversations'
import { conversationOwnerTag, onConversationOwnerChanged, readThread } from '../core/conversations'
import { passCheckResults } from '../core/feed'
import { auditFindingLabel } from '../core/auditReview'
import { checkCounts, recordDigest, surveyRecord } from '../core/recordDigest'
import { surveyEvents } from '../core/survey'
import { closedMark } from '../core/endings'
import { resolveFor, useStore } from '../core/store'
import { stampOf } from '../core/models'
import { researchContractBlock, researchContractFor } from '../core/researchContract'
import { ModelError, streamTextWithReceipt } from '../engine/transport'
import {
  declinedMessage,
  narrationFailed,
  narrationQuietLine,
  narrationStopped,
  watchQuiet,
  type QuietWatch,
} from '../core/failure'
import { bankNarration, closePayer, openPayer } from '../engine/chat'
import { resolvePassEffort } from '../engine/effort'
import { actsStreamFilter, actsTable } from '../engine/acts'
import { refsProtocol } from '../engine/refs'
import { dateLine } from '../engine/prompts'
import { runReadings } from '../core/runFound'
import { reviseAuthored, speakOn } from './askLive'
import { pulseFirstProposal } from './proposalMark'
import { demoOpeningLanded } from '../engine/demo'
import { admissionSentence, predictionSentence } from '../core/conjecture'
import { laterStatements, priorArtLabel, priorArtNote } from '../core/priorArt'
import { SWEEP_KIND_LABEL, sweepSentence } from '../core/sweep'
import { EXECUTABLE_METHOD_LABEL, rangesSentence } from '../core/executableConjecture'
import { executableContractOf, executableOutcome } from '../core/narrationReceipt'
import { hostOf } from '../core/url'
import { STATUS_WORD } from '../core/vocabulary'
import { VOICE_MAX_TOKENS, voiceOn } from '../core/voice'
import { CACHE_WRITE_CLASS, type ModelEntry } from '../core/models'
import { voiceBoundUsd } from '../engine/run'

/* ── The off switch ─────────────────────────────────────────────────────── */

/* It lives in `core/voice.ts` since D295, beside the caps: the engine prices
   the voice into every press, and reads whether it is on from there. The ui's
   own importers keep their door. */
export { onVoiceChange, setVoiceOn, voiceOn } from '../core/voice'

/* ── What just happened — the transitions, pure and testable ────────────── */

/** The slice of the store the triggers read, structural so a test needs no
 *  store. `run` is the live run projection; the session fields are the record. */
export interface NarratorView {
  busy: boolean
  run: { phase: string } | null
  session: Pick<Session, 'id' | 'ops' | 'runs'>
}

/**
 * The pass that just finished, or null. A TRANSITION — the lock fell between
 * these two states, on the same exploration — so a restored session full of
 * finished ops can never read as news, and neither can the state after a
 * document switch.
 */
export function passJustFinished(before: NarratorView, state: NarratorView): Op | null {
  if (!before.busy || state.busy) return null
  if (before.session.id !== state.session.id) return null
  const op = state.session.ops.findLast((row) => !row.narration)
  return op && op.finishedAt !== undefined ? op : null
}

/** The run that just ended, or null — the same transition discipline: the same
 *  run was the newest before, unfinished then, finished now. */
export function runJustEnded(before: NarratorView, state: NarratorView): Run | null {
  if (before.session.id !== state.session.id) return null
  const run = state.session.runs.at(-1)
  const prior = before.session.runs.at(-1)
  return run &&
    prior &&
    prior.id === run.id &&
    prior.finishedAt === undefined &&
    run.finishedAt !== undefined
    ? run
    : null
}

/**
 * Whether a finished pass earns a voice line. The model stamp is the paid-pass
 * test — a pass that spent nothing ran on no model (`beginOp`'s own rule), so
 * the demo's authored unfurls and anything like them stay out. A failed or
 * declined pass keeps its own note in the feed; narrating it would be the app
 * putting words on top of an apology.
 *
 * That holds for a pass that failed PARTWAY too, with nodes already landed. The
 * thread shows its landed lines and its error together, and the voice has no
 * search and no view of the failure beyond the error's own sentence — it could
 * restate what is on screen, or guess at why, and a guess about a failure in a
 * confident voice is worse than the plain note under it. What does need saying
 * after a failure, whether to try again, is the error's to say and the press's
 * to price.
 *
 * Whether it landed anything is the second question (`passLanded`), asked a
 * beat later because a check's product arrives after its op ends.
 */
export function narratableOp(op: Op): boolean {
  return (
    !op.narration &&
    op.finishedAt !== undefined &&
    !op.error &&
    op.refused !== true &&
    op.model !== undefined
  )
}

/** A run worth a wrap-up made at least one move; one stopped before its first
 *  has nothing to say that its own stop line does not. */
export function narratableRun(run: Run): boolean {
  return run.finishedAt !== undefined && run.moves.length > 0
}

/* ── The record, digested for the voice ─────────────────────────────────── */

const clip = (t: string, m: number) => (t.length > m ? `${t.slice(0, m - 1)}…` : t)

/* ── What a pass left behind — read off the record, never off the model ─── */

/**
 * How far before its pass began a result recorded by the SAME press can have
 * landed. `recordOutcome` writes the result onto the node and then starts the
 * re-derivation in one synchronous continuation, so the result predates its
 * pass by the milliseconds between two writes. A result recorded earlier and
 * re-derived from later (`rederiveFromOutcome`) is not this pass's news, and
 * this window is what tells the two apart.
 */
export const OUTCOME_LEAD_MS = 1000

/** Everything one finished pass wrote, in the record's own types. */
export interface PassRecord {
  /** What it landed that is still on the graph. */
  nodes: ReasonNode[]
  /** A source check's own reading, joined the way the feed joins it. */
  verification?: Verification
  /** A panel's votes, when this pass cast them. */
  panel?: PanelCheck
  /** A cross-examination's findings, one per claim it reviewed in this pass. */
  audits: { node: ReasonNode; audit: Audit }[]
  /** A scan's yield — the candidate pairs it added. Null for anything but a scan. */
  scanned: Bridge[] | null
  /** The candidates whose link scan this pass wrote. */
  linked: Bridge[]
  /** A result the person recorded with the press that ran this pass. */
  outcome?: Outcome
  /** The survey of the field this pass wrote on the seed. */
  survey?: FieldSurvey
}

/**
 * One pass's product, read back off the session AFTER it landed. The narrator's
 * digest and its decision to speak at all both read THIS, so the voice cannot
 * be sent to narrate a pass whose record it would then describe as empty, nor
 * kept quiet over a record it could have described.
 *
 * Each record is joined to its pass the way its own surface joins it: checks
 * through `passCheckResults`, the feed's join, so the thread and the voice
 * agree about which reading a pass produced; audits and link scans by the time
 * they were written, which falls inside the pass because both are written
 * before `endOp`; a survey by the op id it carries, on the seed the pass aimed
 * at, whether it is still the live one or a later survey has replaced it.
 */
export function passRecord(
  s: Pick<Session, 'nodes' | 'bridges' | 'verificationArchive'>,
  op: Op,
): PassRecord {
  const target = op.targetIds.length === 1 ? s.nodes[op.targetIds[0]!] : undefined
  const { verification, panel } = passCheckResults(op, target, s.verificationArchive ?? [])
  const during = (at: number) =>
    op.finishedAt !== undefined && at >= op.startedAt && at <= op.finishedAt
  // A branch review aims at a subtree's root and writes its findings onto each
  // claim it reviewed, which its coverage names; an op from before coverage was
  // recorded names only its target.
  const reviewed = op.auditCoverage?.claims.map((c) => c.nodeId) ?? op.targetIds
  const audits =
    op.kind === 'audit'
      ? reviewed.flatMap((id) => {
          const node = s.nodes[id]
          return node?.audit && during(node.audit.at) ? [{ node, audit: node.audit }] : []
        })
      : []
  const outcome =
    op.kind === 'outcome' &&
    target?.outcome &&
    op.finishedAt !== undefined &&
    target.outcome.recordedAt >= op.startedAt - OUTCOME_LEAD_MS &&
    target.outcome.recordedAt <= op.finishedAt
      ? target.outcome
      : undefined
  const survey =
    op.kind === 'survey' && target?.parentId === null
      ? surveyEvents(target).find((row) => row.opId === op.id)
      : undefined
  return {
    nodes: op.produced.map((id) => s.nodes[id]).filter((n) => n !== undefined),
    ...(verification ? { verification } : {}),
    ...(panel ? { panel } : {}),
    audits,
    // The SCAN wears the same kind as realising a candidate, and no target is
    // what tells them apart — a scan reads the whole graph and aims at no node.
    // Its yield is not in `produced` either: it lands in `session.bridges`, and
    // `addBridges` admits only genuinely new pairs, so "created since this pass
    // began" is exactly what this scan added and nothing a prior one did.
    scanned:
      op.kind === 'bridge' && op.targetIds.length === 0
        ? s.bridges.filter((b) => b.createdAt >= op.startedAt)
        : null,
    linked:
      op.kind === 'link' ? s.bridges.filter((b) => b.linkage && during(b.linkage.scannedAt)) : [],
    ...(outcome ? { outcome } : {}),
    ...(survey ? { survey } : {}),
  }
}

/**
 * Whether a finished pass landed anything new — and so whether a narration of
 * it would say more than "nothing happened", for the price of a call.
 *
 * Mechanical, from the stored record alone: a node on the graph, or the record
 * the pass's kind exists to write. Never from what the model said, and never
 * from its trace. A source check, a panel, a cross-examination, a scan's new
 * pairs, a link scan's reading, a result recorded with the press and a survey
 * that kept a returned source are each a whole product with no node in it, so
 * each of them is narrated.
 *
 * Exhaustive with no default, the way `endsTheRun` is: a new kind of pass stops
 * the build until somebody says what counts as its product.
 *
 * A Research Lab pass writes research state, which no arm of the digest reads —
 * so the voice was handed "It landed nothing new" about a comparison or a
 * portfolio the lab had just shown, and a narration built on that could only
 * repeat it, at a price. The lab's own panel is where that result is read; the
 * voice stays quiet there unless the pass landed a node. A conjecture's
 * executable test is filed as a research op too (D283), and its contract IS a
 * record the digest reads (`executableNoteFor`): it is narrated where the join
 * finds that contract, as D283 promised and this switch never let happen (D289).
 */
export function passLanded(
  s: Pick<Session, 'nodes' | 'bridges' | 'verificationArchive' | 'sweeps' | 'research'>,
  op: Op,
): boolean {
  const r = passRecord(s, op)
  if (r.nodes.length > 0) return true
  switch (op.kind) {
    case 'verify':
      return r.verification !== undefined
    case 'panel':
      return r.panel !== undefined
    case 'audit':
      return r.audits.length > 0
    case 'bridge':
      return (r.scanned?.length ?? 0) > 0
    case 'link':
      return r.linked.length > 0
    case 'outcome':
      return r.outcome !== undefined
    case 'expand':
    case 'deepen':
    case 'challenge':
    case 'diverge':
    case 'experiment':
    case 'invert':
    case 'revise':
    case 'rebase':
    case 'portfolio':
      return false
    // Only the executable test, and only where it wrote its contract: a Lab
    // analysis has no contract under its own op id, and a test that came back
    // empty says why in its toast and its feed message.
    case 'research':
      return executableContractOf(s, op) !== null
    // A survey is a whole product with no node in it, like a check, and it is
    // narrated when the gate kept at least one returned source. One that kept
    // none kept no prose either: its toast and the seed's card already say
    // everything was set aside, and a narration could only say it again at a
    // price.
    case 'survey':
      return (r.survey?.sources.length ?? 0) > 0
    // A conjecture pass's product is its conjectures, which are nodes, so one
    // that landed none is counted above with the moves: its toast already says
    // what the admission rules turned away.
    case 'conjecture':
      return false
    // Neither lands a node, and neither is a record: a plan proposes a list that
    // waits on a person's approval, and a sharpen fills the composer's box. There
    // is nothing on the graph for the voice to narrate.
    case 'plan':
    case 'sharpen':
      return false
    // A prior-art search authors no node. Its product is the record it left on
    // the conjecture it searched, under its own op id — narrated like a check's
    // reading, and only where that record landed.
    case 'priorArt': {
      const target = op.targetIds[0] ? s.nodes[op.targetIds[0]] : undefined
      return target?.conjecture?.priorArt?.some((p) => p.opId === op.id) ?? false
    }
    // A field sweep authors no node either: its product is its record on the
    // session, narrated as the seed's survey is — only where it kept an item. One
    // that kept none says so in its toast and on its card, and a narration could
    // only say it again at a price.
    case 'sweep':
      return (s.sweeps?.find((w) => w.opId === op.id)?.items.length ?? 0) > 0
  }
}

/**
 * What the voice is told about a conjecture pass, beyond the list of what landed.
 *
 * The voice is the one surface that describes a conjecture in free prose, and a
 * model narrating a fresh proposal reaches for exactly the words the rest of the
 * app withholds from it. So the record says what a conjecture is, that nothing
 * outside the model has touched it yet, and which words are not the voice's to
 * use — and says it HERE, on the digest, rather than in `NARRATOR_SYSTEM`: those
 * bytes are the cached prefix every narration shares, and a block that rides
 * only on a conjecture pass leaves every other narration exactly as it was.
 *
 * The reader's word for a conjecture is "insight" (D333), and the node list
 * already prints it, so the note uses it too and glosses it: in plain English an
 * insight leans toward a settled finding, which is the one reading the voice must
 * not give it. "A new insight" joins the words that are not the voice's.
 */
function conjectureNote(s: Pick<Session, 'nodes'>, op: Op): string {
  const landed = op.produced
    .map((id) => s.nodes[id])
    .filter((n): n is ReasonNode => n?.kind === 'conjecture' && !!n.conjecture)
  const tally = op.admission ? `Admission: ${admissionSentence(op.admission)}.` : ''
  if (!landed.length) {
    return [
      STATUS_WORD.noConjectureLanded.toUpperCase(),
      tally,
      'An empty insight pass is an answer this pass is allowed to give. A proposal lands only with its whole contract — what it departs from, its leap, a prediction the expectation does not share, and a test that would kill it — and the tally says what became of each one that came back.',
    ]
      .filter(Boolean)
      .join('\n')
  }
  return [
    'THESE ARE INSIGHTS — the app’s name for proposals the model recalls no source stating, each set down with what it departs from and a prediction that would tell the two apart.',
    ...landed.map(
      (n) =>
        `- ${n.label}. It departs from: ${clip(n.conjecture!.departsFrom, 200)} ${clip(predictionSentence(n), 280)}`,
    ),
    tally,
    'Nothing outside the model has touched them yet: no search has looked for them, nothing has attacked them and nothing has tested them. Describe them as proposals and never as findings. Never describe any of them as new or novel or original or unexplored, never as a new insight, and never as a discovery or a breakthrough, because whether anyone has already said one is for a search to report and whether it holds is for a test.',
  ]
    .filter(Boolean)
    .join('\n')
}

/**
 * What the voice is told about a prior-art search, beyond "it landed nothing".
 *
 * The same reason as `conjectureNote`, and sharper: a search that came back
 * without a conjecture is the moment a narrator is likeliest to call the
 * conjecture new. So the record carries the reading in the app's own words, the
 * arithmetic behind it, and which words are not the voice's — on the digest,
 * leaving every other narration byte-identical.
 */
function priorArtNoteFor(s: Pick<Session, 'nodes'>, op: Op): string {
  const target = op.targetIds[0] ? s.nodes[op.targetIds[0]] : undefined
  const search = target?.conjecture?.priorArt?.find((p) => p.opId === op.id)
  if (!search) return 'NO PRIOR-ART RECORD LANDED — the search left nothing on the insight.'
  const listed = search.entries.filter((e) => e.closeness !== 'adjacent').length
  const later = target?.conjecture ? laterStatements(target.conjecture, search) : []
  return [
    `WHAT THE PRIOR-ART SEARCH READ — ${priorArtLabel(search, later)}`,
    priorArtNote(search, undefined, later),
    ...(later.length
      ? [
          `By their own dates, ${later.length} of the pages taking its step ${later.length === 1 ? 'was' : 'were'} written after it was registered. Describe that as a later statement elsewhere, never as the insight being confirmed, vindicated or proven.`,
        ]
      : []),
    `It listed ${listed} page${listed === 1 ? '' : 's'} as stating some of it; ${search.backstop.close} returned page${search.backstop.close === 1 ? ' was' : 's were'} close by term overlap; ${search.dropped} named page${search.dropped === 1 ? '' : 's'} the search never returned ${search.dropped === 1 ? 'was' : 'were'} deleted.`,
    'Describe this as what a search returned and nothing more. Never describe the insight as new or novel or original or unexplored, and never as a discovery or a breakthrough, because a search that came back without it is only a search that came back without it.',
  ].join('\n')
}

/**
 * What the voice is told about a field sweep: how many items, what each page
 * reported in the searching model's one sentence, and that an item is a page's
 * account — never a settled fact, never "new". The quotes stay out: they are the
 * pages' words, and the voice has no use for text it cannot check.
 */
function sweepNoteFor(s: Pick<Session, 'sweeps'>, op: Op): string {
  const sweep = s.sweeps?.find((x) => x.opId === op.id)
  if (!sweep) return 'NO SWEEP LANDED — the search left nothing on the record.'
  return [
    `WHAT THE SWEEP REPORTED — ${sweep.items.length} item${sweep.items.length === 1 ? '' : 's'}`,
    sweepSentence(sweep),
    ...sweep.items.map(
      (i) => `- ${SWEEP_KIND_LABEL[i.kind]}, on ${hostOf(i.url)}: ${clip(i.statement, 240)}`,
    ),
    'Each item is what one page says is unsettled. Describe them as reports of pages and never as settled facts about the field, and never describe anything here as new or novel or a discovery.',
  ].join('\n')
}

/**
 * What the voice is told about a proposed executable test (D283): what the
 * contract measures and the two ranges it declared, that nothing has run it, and
 * who wrote its evaluator — and which words are not the voice's. Only for the
 * research op that wrote a contract: the join is the contract's id, which is
 * that op's id (`executableOutcome`), so another research pass on a conjecture
 * gets nothing from here — and neither the node nor the exploration's id takes
 * part, so a copy of the exploration reads its contract too.
 */
/** The executable test's line under THE PASS THAT JUST FINISHED, where `OP_PAST`
 *  would say the Lab's "Structured research" of the kind it is filed under. */
const EXECUTABLE_PAST = 'Proposed an executable test'

function executableNoteFor(
  s: Pick<Session, 'nodes' | 'research'> & Partial<Pick<Session, 'id'>>,
  op: Op,
): string {
  // The pass being told is the whole ledger the join needs (D290).
  const contract = executableOutcome(
    { id: s.id ?? '', nodes: s.nodes, research: s.research, ops: [op] },
    op,
  )
  if (contract === null || contract === 'none') return ''
  return [
    `AN EXECUTABLE TEST WAS PROPOSED — ${contract.spec.title}`,
    `${contract.design ? `It is ${EXECUTABLE_METHOD_LABEL[contract.design.method]}. ` : ''}It measures ${contract.spec.metric.name} in ${contract.spec.metric.unit}; ${rangesSentence(contract)}, both declared before anything ran.`,
    "Nothing has run it. It waits in Research Lab, where running it is the person's press, in the sandboxed local runner, and the receipt — not a model — will say how the prediction fared. A model wrote its programs, its data and its evaluator, and nobody has reviewed them.",
    'Describe it as a proposed test and nothing more, never as a result. Never describe the insight as new or novel or original, and never as a discovery or a breakthrough.',
  ].join('\n')
}

/**
 * One pass, as the narrator is allowed to know it. Everything here is read
 * back off the session AFTER the pass landed (`passRecord`), so the voice can
 * only describe what the record holds, and the prompt below holds it to
 * exactly that.
 */
export function opDigest(
  s: Pick<Session, 'nodes' | 'seed' | 'bridges' | 'verificationArchive' | 'sweeps' | 'research'> &
    Partial<Pick<Session, 'id'>>,
  op: Op,
): string {
  const target = op.targetIds.length === 1 ? s.nodes[op.targetIds[0]!] : undefined
  const r = passRecord(s, op)
  const produced = r.nodes.map(
    (n) =>
      `- [${kindLabelOf(n)}${n.terminal ? `, ${closedMark(n)}` : ''}] ${n.label} — ${clip(n.claim, 220)}`,
  )
  const { verification: v, panel: p, scanned, outcome, survey: sv } = r
  const SCANNED_SHOWN = 8
  // An executable test's product is its contract, the record below: without this
  // the digest said "It landed nothing new" above the block describing it, under
  // the Lab's "Structured research" (D289).
  const executable = executableContractOf(s, op) !== null
  const recordOnly = v || p || r.audits.length || r.linked.length || outcome || sv || executable
  // The rest of the graph, from the one digest every summary reads, less what
  // the sections above already print in full: this pass's nodes and pairs, and
  // the claim whose record this pass wrote. Without it the voice was asked what
  // a pass "changes — where the tension now sits" and shown only the pass. A
  // scan's digest leaves out EVERY waiting candidate, not just its own: its
  // section already says the rest are in the Overview, and an older pair listed
  // beside the new ones is one the voice would describe as this scan's. A
  // survey leaves out its own line by its op id, not the seed: anything else the
  // seed carries is still the rest of the record.
  const own = new Set([
    ...r.nodes.map((n) => n.id),
    ...(scanned ? s.bridges : []).map((b) => b.id),
    ...r.linked.map((b) => b.id),
    ...(v || p || outcome ? op.targetIds : []),
    ...(sv ? [sv.opId] : []),
  ])
  const rest = recordDigest(s, { exclude: own })

  return `THE SEED THIS ALL SITS UNDER
${s.seed}

THE PASS THAT JUST FINISHED
${
  // `OP_PAST.bridge` says "Synthesised", which is the realisation's word — a
  // scan synthesises nothing, it proposes.
  scanned
    ? 'A scan searched the whole graph for distant claims that might fuse.'
    : `${executable ? EXECUTABLE_PAST : OP_PAST[op.kind]}${target ? ` — on: ${target.label} — ${clip(target.claim, 220)}` : ''}`
}${
    op.readExplorations
      ? `\nIt read ${op.readExplorations} stored exploration${op.readExplorations === 1 ? '' : 's'} from the library besides this one.`
      : ''
  }

${
  scanned
    ? scanned.length
      ? `WHAT THE SCAN PROPOSED — candidate syntheses, now waiting in the Overview tab; nobody has realised any of them${scanned.length > SCANNED_SHOWN ? ` (the first ${SCANNED_SHOWN} of ${scanned.length})` : ''}\n${scanned
          .slice(0, SCANNED_SHOWN)
          .map((b) => `- ${b.title} — ${clip(b.why, 160)}`)
          .join('\n')}`
      : 'The scan proposed no new pairs — everything it saw is already in the Overview or already on the graph.'
    : produced.length
      ? `WHAT LANDED\n${produced.join('\n')}`
      : recordOnly
        ? 'It landed no new nodes — its result is the record below.'
        : 'It landed nothing new.'
}${
    outcome
      ? `\n\nTHE RESULT THE PERSON RECORDED WITH THIS PRESS — it came from the world, and outranks the reasoning\n${clip(outcome.observed, 600)}\n(It went ${outcome.bearing === 'supported' ? 'the way the claim needed' : outcome.bearing === 'refuted' ? 'against the claim' : 'neither way'}.)`
      : ''
  }${
    v
      ? `\n\nWHAT THE SOURCE CHECK FOUND — ${READING_LABEL[v.reading]}, from ${v.sources.length} source${v.sources.length === 1 ? '' : 's'}\n${checkCounts(v)}\n${readingNote(v)}${readingCaveat(v) ? `\n${readingCaveat(v)}` : ''}\n${v.summary ?? 'No summary carried over.'}`
      : ''
  }${
    p
      ? `\n\nTHREE LENSES READ IT SEPARATELY\n${p.votes.map((x) => `- ${x.lens}: ${x.verdict} — ${clip(x.because, 160)}`).join('\n')}`
      : ''
  }${
    r.audits.length
      ? `\n\nWHAT THE CROSS-EXAMINATION PROPOSED — a model from another company reviewed each claim separately; a proposed criticism has not been checked\n${r.audits.map(({ node, audit }) => `- ${node.label}: ${auditFindingLabel(audit)} — ${clip(audit.note, 200)}`).join('\n')}`
      : ''
  }${
    r.linked.length
      ? `\n\nWHAT THE LINK SCAN'S SEARCH SHOWED — a statement about the search, not about the pair\n${r.linked.map((b) => `- ${b.title}: ${LINK_READING_LABEL[b.linkage!.reading]} — ${linkReadingNote(b.linkage!)}`).join('\n')}`
      : ''
  }${
    // Said in the heading, where the voice meets it first. The voice is asked
    // what a pass changes, and a survey's landmark results, unlabelled, read as
    // the question answered.
    sv
      ? `\n\nWHAT THE SURVEY OF THE FIELD RETURNED — a survey of what one search returned about the question, not an answer to it\n${surveyRecord(sv)}`
      : ''
  }${op.kind === 'conjecture' ? `\n\n${conjectureNote(s, op)}` : ''}${
    op.kind === 'priorArt' ? `\n\n${priorArtNoteFor(s, op)}` : ''
  }${op.kind === 'sweep' ? `\n\n${sweepNoteFor(s, op)}` : ''}${(() => {
    const note = op.kind === 'research' ? executableNoteFor(s, op) : ''
    return note ? `\n\n${note}` : ''
  })()}${rest ? `\n\nTHE REST OF THE GRAPH'S RECORD — what this pass landed beside\n${rest}` : ''}`
}

/**
 * One run, tallied the way its report tallies it — `moveResult` is the report's
 * own sentence per move, so the voice and the record cannot disagree — and then
 * the graph's record as the run left it.
 *
 * The tally alone was what the wrap-up used to get: "Challenge on <label>: 4
 * nodes", once per move. After 24 moves the voice could not name a dead end or a
 * check's reading, and guessed wrong about where the dead ends were. The record
 * digest lists them by label, with the run's own work first under each kind —
 * the claims its moves aimed at and the nodes its passes landed.
 */
export function runDigest(
  s: Pick<Session, 'seed' | 'nodes' | 'bridges' | 'ops' | 'verificationArchive'>,
  run: Run,
): string {
  const MOVES_SHOWN = 16
  const moves = run.moves.map((m) => `- ${OP_LABEL[m.kind]} on "${m.label}": ${moveResult(m)}`)
  const passes = new Set(run.moves.map((m) => m.opId))
  const own = new Set([
    ...run.moves.map((m) => m.nodeId).filter(Boolean),
    ...s.ops.filter((o) => passes.has(o.id)).flatMap((o) => o.produced),
  ])
  const record = recordDigest(s, { focus: own })
  // As the gate reads them now, not the words the run copied (`runReadings`).
  const read = runReadings(run, s)
  const readings = read.length
    ? `Source checks read: ${read.map((r) => READING_LABEL[r]).join(', ')}.`
    : ''
  const audits = run.auditVerdicts?.length
    ? `Cross-examination verdicts: ${run.auditVerdicts.join(', ')}.`
    : ''
  const scanned = run.scanned
    ? `The closing scan added ${run.scanned} candidate synthes${run.scanned === 1 ? 'is' : 'es'} to the Overview.`
    : ''
  const conjectured = run.moves
    .filter((m) => m.kind === 'conjecture')
    .reduce((a, m) => a + m.produced, 0)
  const conjectures = run.moves.some((m) => m.kind === 'conjecture')
    ? `Insights proposed: ${conjectured}. Each is a proposal with a prediction and nothing more; never describe one as new or novel or a discovery.`
    : ''

  return `THE SEED THIS ALL SITS UNDER
${s.seed}

THE RUN THAT JUST ENDED
${run.stop ? RUN_STOP_HEAD[run.stop] : 'It ended.'} ${run.moves.length} moves; ${run.produced} node${run.produced === 1 ? '' : 's'} landed, ${run.dead} of them reached an ending in the model's reasoning.
${[readings, audits, conjectures, scanned].filter(Boolean).join('\n')}

ITS MOVES${run.moves.length > MOVES_SHOWN ? ` (the first ${MOVES_SHOWN} of ${run.moves.length})` : ''}
${moves.slice(0, MOVES_SHOWN).join('\n')}${
    record
      ? `\n\nTHE GRAPH'S RECORD AS THE RUN LEFT IT — the run's own work first under each kind\n${record}`
      : ''
  }`
}

/* ── The call ───────────────────────────────────────────────────────────── */

/** What a narration speaks about: one pass, or one run's ending. */
type NarrationParent = NonNullable<Op['narration']>['parentAction']

/**
 * The narrator's contract. The contract itself never changes — the record
 * rides the user turn, and the id table the trailer quotes from rides as the
 * call's `systemTail`, AFTER the cached block rather than inside it. The ask
 * channel's system has the same shape for the same reason — see `askSystem`.
 *
 * That placement is the whole of the cache story here, and it used to be
 * wrong: the table was interpolated into this block under the adapter's one
 * marker, and since a narration fires precisely because a pass has just added
 * nodes, the marked prefix was different on every call. Every narration paid a
 * 1.25× write of the contract, the rules and the table, and nothing ever read
 * the entry back. `voiceSystem` below is now the same bytes for the life of
 * the tab, whatever the graph does.
 *
 * Which is why BOTH shapes live in it — the pass's two paragraphs and the run
 * wrap-up's four — and the user turn only says which one this is
 * (`VOICE_CLOSE`). Two contracts would be two cache entries for one voice.
 *
 * The shapes are word limits, not sentence counts. "Two to four plain
 * sentences" was exceeded by 62 of the 70 narrations a usability study
 * collected, at a median of 152 words; a count of sentences says nothing about
 * how long each one runs. And "don't say whose side a finding is on"
 * is there because the voice told a reader a check was "mostly on your side":
 * the reader has no side here, only claims, and a finding supports or weakens
 * one of those.
 */
const NARRATOR_SYSTEM = `You are the voice of Pathways — a tool that takes an idea apart into a graph of arguable claims. Something the person started has just finished — one pass of reasoning, or a whole run — and its record is in the message below. Tell them what happened, the way a sharp colleague would across a desk.

How you speak:
- After one pass: at most 120 words, in two short paragraphs. The first says what landed; the second, what it changes — where the tension now sits.
- After a run: its one wrap-up, about 200 words, in three short paragraphs — what held up, where it breaks, and what is still open or unchecked.
- Plain paragraphs. No headings, no bold, no lists, and no restating every node — say what actually matters.
- Name a claim by its label in quotes, the way the graph shows it — never by an id or a number. "The parent" or "the seed" is fine.
- Define a technical term in a few words the first time you use it.
- Do not say whose side a finding is on. Say which claim it supports or weakens.
- Do not suggest what to do next, in the app's words or your own. The app's coach names what to try next, by rules it can state; a suggestion from you would be a second one that cannot say why. And you cannot run anything: nothing you say changes the graph.
- When the record says a scan proposed candidate syntheses, they are waiting in the Overview tab — call it by that name, and remember that realising one is the person's press, never yours.

Rules you do not break:
1. The record below is everything you know about what happened. Do not invent findings it does not contain, and do not soften ones it does.
2. You have no search on this turn and have read no page. Never write a URL or a citation; if something needs a source, say a source check would settle it.
3. Never say a claim is verified or refuted unless the record says a source check read it that way. A reading from a check, or a result a person recorded, outranks reasoning — including yours. A check reports a reading of its sources, not a verdict, and a confidence figure is the model's own rating, not a probability.`

/**
 * The last words of the user turn: which of the contract's two shapes this
 * narration owes. Keyed by the narration's own parent kind, so a third kind of
 * parent stops the build until somebody decides how long it may speak.
 */
export const VOICE_CLOSE: Record<NarrationParent['kind'], string> = {
  operation:
    'Say what happened and what it changes: at most 120 words, in two paragraphs, and no next move.',
  run: 'This is the run’s one wrap-up: about 200 words, in three paragraphs — what held up, where it breaks, and what is still open — and no next move.',
}

/**
 * The cached block, as a function of nothing: the contract and the marks
 * rule, with the id table the rule quotes left to `systemTail`. Exported so a
 * test can assert that no word of the graph reaches it — the same property
 * `prompts.test.ts` holds over the derive system block, for the same reason.
 *
 * The protocol it once carried asked the voice to end on its suggested moves as
 * buttons, and the voice suggests nothing now (D295). What rides instead is the
 * one the ask channel's answers follow: a sentence that rests on a node ends
 * with a mark the surface turns into a press back to it (`engine/refs.ts`).
 */
export const voiceSystem = (): string =>
  `${NARRATOR_SYSTEM}\n\n${'═'.repeat(60)}\n\n${refsProtocol()}`

/** What a narration is for, as its receipt and its contract state it. */
const voicePurpose = (parentAction: NarrationParent): string =>
  `Narrate the recorded result of ${parentAction.kind} ${parentAction.id}; explain what happened without proposing a next step or creating research findings.`

/**
 * Everything one narration sends, built from the session captured at the
 * transition that earned it: the cached contract, the id table its marks may
 * quote, and the user turn — the contract block, the date, the record and the
 * shape it owes. One builder, for the call and for anything that measures it:
 * the ceiling's check (`voiceFitsCeiling`) and the test that sizes the figure
 * every press is quoted at (`VOICE_INPUT_TOKENS`).
 */
export function voiceRequest(
  sess: Session,
  record: string,
  parentAction: NarrationParent,
  targetIds: readonly string[] = [],
  now = Date.now(),
): {
  purpose: string
  contract: ReturnType<typeof researchContractFor>
  system: string
  systemTail: string
  user: string
} {
  const purpose = voicePurpose(parentAction)
  const contract = researchContractFor(sess, 'research', targetIds, purpose)
  return {
    purpose,
    contract,
    system: voiceSystem(),
    systemTail: actsTable(sess),
    user: `${researchContractBlock(contract)}${dateLine(now)}\n\n${record}\n\n${VOICE_CLOSE[parentAction.kind]}`,
  }
}

/** A request's size as the bound reads it: UTF-8 bytes, which no tokenizer
 *  undercuts. */
export const voiceRequestBytes = (
  r: Pick<ReturnType<typeof voiceRequest>, 'system' | 'systemTail' | 'user'>,
): number => new TextEncoder().encode(`${r.system}${r.systemTail}${r.user}`).length

/**
 * Whether a run's summary fits under the ceiling the run was given (D295).
 *
 * The voice is a follower of the run press, so a ceiling the person set binds
 * it too — and it YIELDS: the moves were what the ceiling bought, so the run is
 * never cut short to make room for its summary; the summary is dropped when its
 * bound does not fit in what the run left. No ceiling, no question. A ceiling
 * with nothing to measure against — a run with no estimate, a voice with no
 * published rate — cannot be shown to hold, so it refuses: the direction a
 * ceiling fails in is silence, never a charge past it.
 */
export function voiceFitsCeiling(
  run: Pick<Run, 'budget' | 'estimatedUsd'>,
  voice: Pick<ModelEntry, 'price' | 'provider'>,
  requestBytes: number,
): boolean {
  const ceiling = run.budget.usd
  if (ceiling === undefined) return true
  if (run.estimatedUsd === undefined || !voice.price) return false
  const bound = voiceBoundUsd(requestBytes, voice.price, CACHE_WRITE_CLASS[voice.provider], 'run')
  return run.estimatedUsd + bound <= ceiling
}

/** Ids already spoken for, so a narration can never double — including one
 *  QUEUED and then superseded, which was still its op's one chance. Ids are
 *  minted once across explorations, so this never needs resetting. */
const narrated = new Set<string>()

/**
 * One voice at a time, and a queue of ONE. Narrations are seconds long and
 * manual passes are slower than that, so the queue almost never holds anything
 * — but a run can end while a lone pass's line is still arriving, and dropping
 * the newer of the two on the floor would silence the wrap-up the run message
 * promises. The OLDER pending one is the honest casualty: its record is on
 * screen either way — `drop` is how it gives its payer back on the way out.
 */
let speaking = false
let pending: { run: () => void; drop: () => void } | null = null

/**
 * A line queued for one reader is not spoken to the next. The narration under
 * way is stopped by `askLive`, which holds its flight; the one waiting behind it
 * was earned by the last reader's pass and would otherwise start the moment
 * that stop lands, into the conversation of whoever signed in (A01). Given back
 * the way a superseded line is, so its payer is settled. Subscribed at load
 * because it only ever drops a line — it spends nothing, which is the reason
 * the voice itself waits for `armNarrator`.
 */
onConversationOwnerChanged(() => {
  pending?.drop()
  pending = null
})

function narrate(
  sessionId: string,
  about: StoredAbout | undefined,
  record: string,
  parentAction: NarrationParent,
  targetIds: string[] = [],
  /** The run a wrap-up follows, for its ceiling. */
  run?: Pick<Run, 'budget' | 'estimatedUsd'>,
): void {
  const s = useStore.getState()
  const m = resolveFor(s, 'chat')
  // No key for the chat role: quiet, never a fallback (invariant 4). The turn
  // is not minted at all, so nothing half-said lands in the feed — and checked
  // BEFORE the lock is taken, because an early return from inside it left
  // `speaking` latched true and the voice dead for the rest of the tab.
  if (!m) return
  // A run's ceiling binds its summary, which yields to the moves (D295): the
  // bound is taken over the request about to be sent, before anything is minted,
  // and a summary that does not fit is never started. A request that cannot be
  // built at all has nothing to fit.
  if (run && run.budget.usd !== undefined) {
    let bytes: number | null
    try {
      bytes = voiceRequestBytes(voiceRequest(s.session, record, parentAction, targetIds))
    } catch {
      bytes = null
    }
    if (bytes === null || !voiceFitsCeiling(run, m.entry, bytes)) return
  }
  // Everything the call needs is captured NOW, at the transition that earned
  // the line — the model, the graph this narration is about, and above all the
  // payer — because a QUEUED narration may not run until after the reader has
  // switched explorations, and each of these must belong to the pass's own
  // moment, not to whatever is open when the queue drains. The payer is the
  // receipt's whole point (D38): minted here, the paying set follows renames,
  // so the charge lands on the exploration whose pass this narrates.
  const payer = openPayer()
  const sess = s.session
  const job = () => {
    speaking = true
    void (async () => {
      // Its own controller, and a Stop that reaches only it: the Stop under its
      // words (`stopNarration`) and the phone box's (`stopLive`). A narration
      // stalled after a few words otherwise held the feed's box shut for as
      // long as the transport's ten minutes, and the box's Stop, which aborts
      // the Ask channel's request, did nothing to it. Stopping spends nothing,
      // and never reaches the pass this follows (D51).
      const controller = new AbortController()
      const speak = speakOn(sessionId, 'feed', about, () => controller.abort())
      // No trailer is asked for (D295), and none is kept: the filter stays so
      // a line a model writes anyway is cut where `narrationWords` cuts it on
      // the stored receipt, and the words the reader watched arrive are the
      // words a replay of that receipt shows.
      const filter = actsStreamFilter()
      const purpose = voicePurpose(parentAction)
      const operation: Op = {
        id: `narration-${crypto.randomUUID()}`,
        kind: 'research',
        targetIds,
        startedAt: Date.now(),
        produced: [],
        model: stampOf(m.entry),
        narration: { purpose, parentAction },
      }
      let retained = false
      // The quiet watch every live request has (`watchQuiet`), said under the
      // voice's words in its own sentence, beside its Stop: while it is live the
      // feed's box holds questions back, so a narration stalled after a few
      // words read as the voice still talking.
      let quiet: QuietWatch | null = null
      try {
        const req = voiceRequest(sess, record, parentAction, targetIds)
        const contract = req.contract
        if (!bankNarration(operation, payer))
          throw new Error('The narration intent could not be retained.')
        quiet = watchQuiet((ms) => speak.note(ms === null ? null : narrationQuietLine(ms)))
        const res = await streamTextWithReceipt(
          {
            model: m,
            system: req.system,
            // Graph content, so it rides outside the cached block: the ids the
            // marks may quote. Built from the session captured at the
            // transition, like everything else here.
            systemTail: req.systemTail,
            user: req.user,
            // The `chat` row of the effort table: the voice speaks on the ask
            // channel under all of that channel's rules, and this is that
            // channel's level — a paragraph about text the reader can already
            // see, where the reasoning verbs are where effort belongs.
            effort: resolvePassEffort({ pass: 'chat', levels: s.effortLevels }),
            maxTokens: VOICE_MAX_TOKENS[parentAction.kind],
            maxRetries: 0,
            cacheKey: 'voice',
            signal: controller.signal,
            onText: (d) => {
              quiet?.heard()
              const out = filter.push(d)
              if (out) speak.append(out)
            },
          },
          {
            id: `${operation.id}:call:1`,
            ordinal: 1,
            record: (receipt) => {
              retained = bankNarration(
                {
                  ...operation,
                  finishedAt: receipt.completedAt,
                  modelCalls: [{ ...receipt, researchContract: contract }],
                  ...(receipt.outcome.kind === 'failed' ? { error: receipt.outcome.reason } : {}),
                  ...(receipt.outcome.kind === 'refused' ? { refused: true } : {}),
                },
                payer,
              )
            },
          },
        )
        const rest = filter.flush()
        if (rest) speak.append(rest)
        const { shown } = filter.result()
        speak.finish(
          shown,
          res.refused
            ? declinedMessage('The model declined to narrate that.', res, res.spend.model, 'chat')
            : undefined,
        )
      } catch (err) {
        if (!retained)
          bankNarration(
            {
              ...operation,
              finishedAt: Date.now(),
              error:
                'No terminal narration receipt was retained; inspect this attempt before continuing.',
            },
            payer,
          )
        const partial = filter.result().shown
        if (err instanceof ModelError) {
          // An auth failure here deliberately does NOT open the setup sheet —
          // the narrator is background; a modal it summons would interrupt the
          // very work it is narrating. The error rides the turn instead, in the
          // narration's own words: the adapter's "wait a minute, then try again"
          // was advice about a button the voice does not have. An abort is the
          // reader's Stop — the deadline's is reclassified as `timeout` — and it
          // used to be finished as though the voice had said all it meant to,
          // then stored that way: half a narration, replayed as a whole one.
          speak.finish(
            partial,
            err.kind === 'aborted'
              ? narrationStopped(partial.trim().length > 0)
              : narrationFailed(err.kind),
          )
        } else {
          speak.finish(partial, 'Something went wrong talking to the model.')
        }
      } finally {
        quiet?.stop()
        closePayer(payer)
        speaking = false
        const next = pending
        pending = null
        next?.run()
      }
    })()
  }
  if (speaking) {
    // Superseding the older pending line must give its payer back — a payer
    // nothing will ever settle would sit in the paying set for the tab's life.
    pending?.drop()
    pending = { run: job, drop: () => closePayer(payer) }
    return
  }
  job()
}

/* ── The worked example's line ──────────────────────────────────────────── */

/**
 * The demo's id, a literal for `core/conversations.ts`'s reason: this module
 * reads what others own and keeps its imports pointed one way.
 */
const DEMO_ID = 's_demo'

/**
 * Authored, and it says so. The demo spends nothing and runs on no model, so a
 * model-written line here would be a forgery of the very voice it advertises —
 * the same honesty that keeps the demo's verifications structural
 * (`demo.ts`). It still STREAMS, because the demo's job is to show the
 * experience, and the experience is a voice arriving.
 *
 * The two moves it names are the two chips below, and both are AUTHORED
 * RESERVES (`hasDemoReserve`) — press one and a pre-written branch unfurls,
 * spending nothing — so the demo's first proposal row actually works instead
 * of walking a keyless reader into a key prompt. It is the one voice line that
 * names a move: the demo's orientation, authored and labelled as such, while
 * its last sentence says that the model's own voice does not (D295).
 */
export const DEMO_VOICE =
  'Three different machines are hiding in the word “antigravity” — cutting weight, cutting inertia, and thrust without reaction mass — and the graph now holds all three. Weight reduction is the only one with a live experimental path, and the 2024 positive-energy soliton is the one hopeful result on the exotic side that nobody here has stress-tested. If this were mine I would Diverge on the weight-reduction branch to get around the hovering floor price, and Challenge the soliton claim before believing it. This line is part of the worked example. Connect a key and the model sums up each pass and each run in its own words, while the coach names what to try next by rules it can state.'

/**
 * Every earlier wording of `DEMO_VOICE` that shipped, oldest first. A shelf
 * holding one of them is REVISED in place to the current wording
 * (`reviseAuthored`) rather than spoken a second time beneath itself. Only
 * authored lines are listed here, which is what makes revising them honest: the
 * model's words are never edited. A new wording that ships adds the one it
 * replaces to this list.
 */
export const DEMO_VOICE_EARLIER: readonly string[] = [
  'Three different machines are hiding in the word “antigravity” — cutting weight, cutting inertia, and thrust without reaction mass — and the graph now holds all three. Weight reduction is the only one with a live experimental path, and the 2024 positive-energy soliton is the one hopeful result on the exotic side that nobody here has stress-tested. If this were mine I would Diverge on the weight-reduction branch to get around the hovering floor price, and Challenge the soliton claim before believing it. This line is part of the worked example — connect a key and the voice is the model’s own.',
]

/**
 * What the worked example's opening owes the shelf: nothing when the current
 * line is already there, a revision when an earlier wording is, and the line
 * itself otherwise. Pure over the stored turns, so the choice a reload makes is
 * the one a test can state. Only a VOICE turn can match — a reader's question
 * that happens to quote the line is theirs.
 */
export function demoLineDue(
  shelf: readonly Pick<StoredTurn, 'id' | 'answer' | 'voice'>[],
): { kind: 'spoken' } | { kind: 'revise'; id: string } | { kind: 'speak' } {
  if (shelf.some((t) => t.voice && t.answer === DEMO_VOICE)) return { kind: 'spoken' }
  const stale = shelf.find((t) => t.voice && DEMO_VOICE_EARLIER.includes(t.answer))
  return stale ? { kind: 'revise', id: stale.id } : { kind: 'speak' }
}

/** The pressable form of exactly the moves the line names — both backed by
 *  authored reserves, so they run in the demo. The same loose stored shape as
 *  every proposal row; `ActRow` re-validates them like any other. */
const DEMO_ACTS: NonNullable<StoredTurn['acts']> = [
  { kind: 'diverge', node: 'def-target', why: 'roads around the hovering floor price' },
  { kind: 'challenge', node: 'soliton', why: 'stress the one hopeful result' },
]

/** The demo's boot unfurl: an unpaid pass on the seed that landed the graph. */
export function demoSeedOp(s: Pick<Session, 'id' | 'nodes'>, op: Op): boolean {
  const target = op.targetIds[0] ? s.nodes[op.targetIds[0]] : undefined
  return (
    s.id === DEMO_ID &&
    op.model === undefined &&
    target?.parentId === null &&
    op.produced.length > 0
  )
}

/** Word-by-word, on a timer — no model, no spend, and cut short cleanly if the
 *  exploration is replaced mid-line. The chips arrive only with the WHOLE
 *  line: proposals under a sentence that visibly stopped short would be
 *  buttons hanging off half a thought. It wears the voice's Stop like any
 *  narration — a live voice turn that ignored it would be the same dead
 *  button — and a stopped line ends the way a stopped narration does. */
function speakAuthored(
  sessionId: string,
  about: StoredAbout | undefined,
  line: string,
  acts?: NonNullable<StoredTurn['acts']>,
): void {
  const words = line.split(' ')
  let i = 0
  const speak = speakOn(sessionId, 'feed', about, () => {
    clearInterval(timer)
    speak.finish(words.slice(0, i).join(' '), narrationStopped(i > 0))
  })
  const timer = setInterval(() => {
    if (useStore.getState().session.id !== sessionId || i >= words.length) {
      clearInterval(timer)
      const whole = i >= words.length
      // Cut short only by the reader leaving — the one other way out of this
      // branch — which is no landing away and leaves no dot (D334).
      speak.finish(words.slice(0, i).join(' '), undefined, whole ? acts : undefined, {
        cutByLeaving: !whole,
      })
      if (whole) pulseFirstProposal(sessionId, acts)
      return
    }
    speak.append((i ? ' ' : '') + words[i])
    i++
  }, 26)
}

/* ── The watcher ────────────────────────────────────────────────────────── */

const graphAbout = (s: Pick<Session, 'seed'>): StoredAbout => {
  const seed = s.seed.trim()
  return {
    kind: 'graph',
    label: seed.length > 80 ? `${seed.slice(0, 79)}…` : seed || 'This exploration',
  }
}

const opAbout = (s: Session, op: Op): StoredAbout => {
  const target = op.targetIds[0] ? s.nodes[op.targetIds[0]] : undefined
  if (target && target.parentId !== null) {
    return { kind: 'node', id: target.id, label: target.label }
  }
  const archived =
    op.kind === 'verify'
      ? (s.verificationArchive ?? []).find(
          (row) => row.nodeId === op.targetIds[0] && row.verification.opId === op.id,
        )
      : undefined
  return archived?.verification.labelAtCheck
    ? {
        kind: 'node',
        id: archived.nodeId,
        label: archived.verification.labelAtCheck,
      }
    : graphAbout(s)
}

let armed = false

/**
 * Wire the voice to the store, once. An explicit call from App's boot rather
 * than a module-load side effect, because this module SPENDS: a subscription
 * that begins the moment anything imports a helper from this file would be an
 * authorisation nobody can point to.
 */
export function armNarrator(): void {
  if (armed) return
  armed = true
  useStore.subscribe((state, before) => {
    if (!voiceOn()) return

    const run = runJustEnded(before, state)
    if (run && narratableRun(run) && !narrated.has(run.id)) {
      narrated.add(run.id)
      narrate(
        state.session.id,
        graphAbout(state.session),
        runDigest(state.session, run),
        { kind: 'run', id: run.id },
        [],
        run,
      )
      return
    }

    const op = passJustFinished(before, state)
    if (!op || narrated.has(op.id)) return
    // This press purchased its own retained report, with no paid follower.
    if (state.session.research?.assessments?.attempts.some((a) => a.opId === op.id)) return
    // Mid-run passes stay quiet — the run speaks once, at the end. The run
    // projection outlives its ending with phase 'ended', so this reads the
    // live phases only.
    if (state.run !== null && state.run.phase !== 'ended') return
    if (narratableOp(op)) {
      narrated.add(op.id)
      const sid = state.session.id
      // The digest is built ONE MICROTASK later, not at the transition itself:
      // a source check ends its op first and patches the verification onto the
      // node a few lines after (`reason.ts`), inside the same synchronous
      // continuation — so the state at the endOp transition is missing the
      // very finding the pass exists to produce, and a digest built here
      // described a finished check as having landed nothing. The queue drains
      // after that whole continuation; the re-read below sees the patched
      // node. The op's own fields are settled at endOp, so only the SESSION is
      // re-read — guarded on it still being the one whose pass ended.
      // …and on it still being the same READER's: a sign-out landing in the
      // gap must not have the last reader's pass narrated to the next one.
      const reader = conversationOwnerTag()
      queueMicrotask(() => {
        const s = useStore.getState()
        if (s.session.id !== sid) return
        if (conversationOwnerTag() !== reader) return
        // A pass that landed nothing new gets no line: the call would cost as
        // much as any narration and could only say that nothing happened. Asked
        // HERE, after the same re-read, because a source check's product is the
        // very record that is patched on a beat after `endOp`.
        if (!passLanded(s.session, op)) return
        narrate(
          sid,
          opAbout(s.session, op),
          opDigest(s.session, op),
          { kind: 'operation', id: op.id },
          op.targetIds,
        )
      })
      return
    }
    if (demoSeedOp(state.session, op)) {
      narrated.add(op.id)
      // The line reads the WHOLE opening — "the graph now holds all three" —
      // and an opening stopped short, by Stop or by a door that stopped it to
      // go somewhere else, does not hold it. Said over that, it names chips
      // for nodes that never landed; begun just before a door moves on, it
      // leaves a few words on the shelf that the replay then never matches.
      if (!demoOpeningLanded(state.session)) return
      // Replayed demos replay this transition, and the line is already on the
      // shelf from last time — the conversation would stack the same words
      // once per reload. A shelf holding an EARLIER wording has it revised in
      // place, for the same reason (`demoLineDue`).
      const due = demoLineDue(readThread(state.session.id, 'feed'))
      if (due.kind === 'revise')
        reviseAuthored(state.session.id, 'feed', due.id, DEMO_VOICE, DEMO_ACTS)
      else if (due.kind === 'speak')
        speakAuthored(state.session.id, graphAbout(state.session), DEMO_VOICE, DEMO_ACTS)
    }
  })
}
